import { useLocationPathname } from '@rocketlane/rocket-router';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useState } from 'react';
import {
  BehaviorSubject,
  concat,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { AccountId, UserId } from 'api/models';
import { notifications } from 'api/notifications';
import { Action, EventType, ObjectType, pushes, UserChannel } from 'api/pushes';
import { queryClient } from 'queries';
import {
  NOTIFICATION_DATA_QUERY_KEY,
  useAccountDataQuery,
  useNotificationDataQuery,
} from 'queries/account';
import { logger } from 'services';

type Authorized = ['authorized', UserId, AccountId];
const Authorized = (userId: UserId, accountId: AccountId): Authorized => [
  'authorized',
  userId,
  accountId,
];

// BehaviourSubject is a hot observable that emits the last value to new subscribers
// and the current value to all subscribers when a new value is emitted.
// https://rxjs.dev/guide/subject#behaviorsubject
const _auth$ = new BehaviorSubject<Authorized | null>(null);
const _lastSeenAt$ = new BehaviorSubject<Moment | null>(null);

// Subject is a hot observable that emits the current value to all subscribers
// when a new value is emitted. It does not emit the last value to new subscribers.
// https://rxjs.dev/guide/subject#subject
const _seen$ = new Subject<Moment>();

// Observable of the last time the user has seen notifications.
const lastSeenAt$ = _lastSeenAt$.asObservable().pipe(
  filter((x) => x != null),
  map((x) => x as Moment),
  shareReplay(1),
);

// Observable of authorization status.
// This is a hot observable that emits the current authorization status
// and all future authorization status changes.
// The current authorization status is emitted immediately.
// The current authorization status is null if the user is not authorized.
const auth$ = _auth$.asObservable().pipe(
  filter((x) => x != null),
  map((x) => x as Authorized),
  // Replay the last authorization status. more on shareReplay here https://www.learnrxjs.io/learn-rxjs/operators/multicasting/sharereplay
  shareReplay(1),
);

// initailaize the auth observable. This will emit the current authorization status immediately.
export const initialize = (userId: UserId, accountId: AccountId): void => {
  if (_auth$.value !== null) {
    return;
  }
  _auth$.next(Authorized(userId, accountId));
};

// merge the last seen at observable with the seen observable.
export const handleLastSeenAtSubscription = (): Subscription =>
  merge(
    auth$.pipe(switchMap(() => notifications.lastSeenAt())),
    // when the user sees notifications, update the last seen at observable.
    _seen$.pipe(
      // switchmap cancels the previous request and starts a new one.
      // https://www.learnrxjs.io/learn-rxjs/operators/transformation/switchmap
      switchMap(() => notifications.seen()),
      switchMap(() => concat(of(moment()), notifications.lastSeenAt())),
    ),
  ).subscribe(
    (at) => _lastSeenAt$.next(at),
    (error) => {
      logger.error('fetching last seen at failed', { error });
    },
  );

// export seen observable to be used in useNotificationsStore
export const seen = (at: Moment): void => {
  _seen$.next(at);
};

// export lastSeenAt observable to be used in other files.
export const lastSeenAt = (): Observable<moment.Moment> => lastSeenAt$;

/**
 * custom hook to get the pending notifications count.
 * The pending notifications count will be updated inside the useAccountDataQuery hook.
 * This hook will return the pending notifications count from the queryClient cache.
 * every time the queryClient cache is updated via websocket, The pending notifications count will be incremented
 * @param  {}
 * @returns [number, () => void]
 */
export const usePending = (): [number, () => void] => {
  // get the current authorization status.
  const { data: accountData } = useAccountDataQuery();
  const [newNotfication, setNewNotificaiton] = useState(
    accountData?.newNotificationAvailable,
  );

  useEffect(() => {
    setNewNotificaiton(accountData?.newNotificationAvailable);
  }, [accountData?.newNotificationAvailable]);

  const { data: notificationData } = useNotificationDataQuery();
  const { accountId } = accountData?.account || {};
  const { userId } = accountData?.userProfile?.user || {};

  const pathname = useLocationPathname();

  useEffect(() => {
    function notificastionHandler() {
      // update the query client cache with the pending notifications count.
      // set the newNotificationAvailable property to false after the first render
      // the newNotificationAvailable should be used only for the firrst render
      // this way we can avoid the inital api call for lastSeenAt

      if (
        notificationData?.pendingNotifications === 0 ||
        pathname.includes('notifications')
      ) {
        queryClient.setQueryData(NOTIFICATION_DATA_QUERY_KEY, {
          // increment the pending notifications count by 1 when a new notification is received.
          pendingNotifications:
            (notificationData?.pendingNotifications || 0) + 1,
          // set the newNotificationAvailable property to false after the first render
          newNotificationAvailable: false,
        });
      }
    }

    // subscribe to notification channel

    // check if subscription is already created
    if (accountId && userId) {
      EventType(Action.Create, ObjectType.Notification);
      pushes<Notification>(
        UserChannel(AccountId(accountId), UserId(userId)),
        EventType(Action.Create, ObjectType.Notification),
      ).subscribe(notificastionHandler);
    }
  }, [accountId, userId, notificationData, pathname]);

  // expose a methiod to reset the pending notifications count.
  const resetPending = useCallback(() => {
    queryClient.setQueryData(NOTIFICATION_DATA_QUERY_KEY, {
      pendingNotifications: 0,
      newNotificationAvailable: false,
    });

    setNewNotificaiton(false);
  }, []);

  // On page load, the pending notification clunt should be taken from newNotificationAvailable property in init call
  if (newNotfication) {
    return [1, resetPending];
  }

  // return the pending notifications count and the resetPending method
  const pendingNotifications = notificationData?.pendingNotifications || 0;

  return [pendingNotifications, resetPending];
};
